import React, { useState, useEffect } from 'react';
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import SearchIcon from "@mui/icons-material/Search";
import InputBase from "@mui/material/InputBase";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import { ResponsivePie } from '@nivo/pie';
import { getAllClients } from '../../api/clientApi';
import { useAuth } from '../../context/AuthContext';

const Summary = ({ fromDate = null, toDate = null, onStatsReady }) => {
  const { user } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [loading, setLoading] = useState(true);
  const [aggregatedData, setAggregatedData] = useState({
    totalRevenue: 0,
    totalMaterialPurchases: 0,
    totalLabourCharges: 0,
    netProfit: 0
  });
  const [clickedArc, setClickedArc] = useState(null);
  const [hoveredArc, setHoveredArc] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedSegment, setSelectedSegment] = useState(null);
  const [breakdownTabValue, setBreakdownTabValue] = useState(0);
  const [breakdownData, setBreakdownData] = useState({
    materialPurchases: [],
    labourCharges: []
  });
  const [clientWiseBreakdownData, setClientWiseBreakdownData] = useState({
    materialPurchases: [],
    labourCharges: []
  });
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [vendorDrillDownData, setVendorDrillDownData] = useState([]);
  const [vendorClientMap, setVendorClientMap] = useState({});
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    const fetchAndAggregateData = async () => {
      try {
        setLoading(true);

        // Fetch all clients for the company
        if (!user?.company_id) return;

        const params = {
          company_id: user.company_id
        };

        // Filter by the selected Financial Year (same date-range logic as All Clients)
        if (fromDate && toDate) {
          params.fromDate = fromDate;
          params.toDate = toDate;
        }

        const response = await getAllClients(params);

        if (response.success) {
          // Aggregate data across all clients
          let totalMaterialPurchases = 0;
          let totalLabourCharges = 0;
          let totalRevenue = 0;
          const materialPurchasesBreakdown = [];
          const labourChargesBreakdown = [];
          const clientWiseMaterialPurchases = [];
          const clientWiseLabourCharges = [];
          const vendorToClientMap = {}; // Track vendor -> clients mapping

          response.data.forEach(client => {
            const balanceSheet = client.client_balance_sheet || {};
            const summaryDetails = balanceSheet.summary_details || {};

            // Income
            const incomeRelated = summaryDetails.income_related || {};
            const initialContractValue = parseFloat(incomeRelated['Initial Contract Value']) || 0;
            const additionalWorkValue = parseFloat(incomeRelated['Additional Work Value']) || 0;
            totalRevenue += initialContractValue + additionalWorkValue;

            // Expenses
            const expensesRelated = summaryDetails.expenses_related || {};
            totalMaterialPurchases += parseFloat(expensesRelated['Total Material Purchases']) || 0;
            totalLabourCharges += parseFloat(expensesRelated['Total Labour Charges']) || 0;

            // Collect material purchases breakdown - Vendor-wise
            const materialPurchases = balanceSheet.material_purchases || {};
            let clientMaterialTotal = 0;
            Object.entries(materialPurchases).forEach(([key, value]) => {
              if (value.vendor_name && value.amount_paid) {
                const amount = parseFloat(value.amount_paid) || 0;
                clientMaterialTotal += amount;

                const existingVendor = materialPurchasesBreakdown.find(v => v.vendorName === value.vendor_name);
                if (existingVendor) {
                  existingVendor.amount += amount;
                } else {
                  materialPurchasesBreakdown.push({
                    vendorName: value.vendor_name,
                    amount: amount
                  });
                }

                // Store vendor-to-client mapping for drill-down
                const vendorKey = `${value.vendor_name}_material`;
                if (!vendorToClientMap[vendorKey]) {
                  vendorToClientMap[vendorKey] = [];
                }
                vendorToClientMap[vendorKey].push({
                  clientName: client.client_name,
                  amount: amount
                });
              }
            });

            // Collect labour charges breakdown - Vendor-wise
            const labourCharges = balanceSheet.labour_charges || {};
            let clientLabourTotal = 0;
            Object.entries(labourCharges).forEach(([key, value]) => {
              if (value.vendor_name && value.amount_paid) {
                const amount = parseFloat(value.amount_paid) || 0;
                clientLabourTotal += amount;

                const existingVendor = labourChargesBreakdown.find(v => v.vendorName === value.vendor_name);
                if (existingVendor) {
                  existingVendor.amount += amount;
                } else {
                  labourChargesBreakdown.push({
                    vendorName: value.vendor_name,
                    amount: amount
                  });
                }

                // Store vendor-to-client mapping for drill-down
                const vendorKey = `${value.vendor_name}_labour`;
                if (!vendorToClientMap[vendorKey]) {
                  vendorToClientMap[vendorKey] = [];
                }
                vendorToClientMap[vendorKey].push({
                  clientName: client.client_name,
                  amount: amount
                });
              }
            });

            // Collect client-wise breakdown
            if (clientMaterialTotal > 0) {
              clientWiseMaterialPurchases.push({
                clientName: client.client_name,
                amount: clientMaterialTotal
              });
            }

            if (clientLabourTotal > 0) {
              clientWiseLabourCharges.push({
                clientName: client.client_name,
                amount: clientLabourTotal
              });
            }
          });

          const netProfit = totalRevenue - totalMaterialPurchases - totalLabourCharges;

          setAggregatedData({
            totalRevenue,
            totalMaterialPurchases,
            totalLabourCharges,
            netProfit: Math.max(0, netProfit)
          });

          setBreakdownData({
            materialPurchases: materialPurchasesBreakdown,
            labourCharges: labourChargesBreakdown
          });

          setClientWiseBreakdownData({
            materialPurchases: clientWiseMaterialPurchases,
            labourCharges: clientWiseLabourCharges
          });

          setVendorClientMap(vendorToClientMap);
        }
      } catch (error) {
        console.error("Error fetching clients data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchAndAggregateData();
  }, [user, fromDate, toDate]);

  // Report the aggregated stats up so the parent hero can render its own
  // stat cards without re-fetching/re-aggregating the same data.
  useEffect(() => {
    if (onStatsReady) onStatsReady(aggregatedData);
  }, [aggregatedData, onStatsReady]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  // Helpers for the crafted stat cards
  const rev = aggregatedData.totalRevenue || 0;
  const pctOf = (v) => (rev > 0 ? ((v / rev) * 100).toFixed(1) : '0');
  const compactINR = (n) => {
    const num = Number(n) || 0;
    if (num >= 10000000) return '₹' + (num / 10000000).toFixed(2) + ' Cr';
    if (num >= 100000) return '₹' + (num / 100000).toFixed(2) + ' L';
    return '₹' + num.toLocaleString('en-IN');
  };
  const statCards = [
    { label: 'Total Revenue', value: rev, color: '#C4B106', sub: 'Contract value', segment: 'Total Revenue' },
    { label: 'Material Purchases', value: aggregatedData.totalMaterialPurchases, color: '#1E40AF', sub: pctOf(aggregatedData.totalMaterialPurchases) + '% of revenue', segment: 'Total Material Purchases' },
    { label: 'Labour Charges', value: aggregatedData.totalLabourCharges, color: '#DB2777', sub: pctOf(aggregatedData.totalLabourCharges) + '% of revenue', segment: 'Total Labour Charges' },
    { label: 'Gross Profit', value: aggregatedData.netProfit, color: '#059669', sub: pctOf(aggregatedData.netProfit) + '% margin', segment: 'Gross Profit' },
  ];
  // Which segments have a vendor/client drill-down
  const hasBreakdown = selectedSegment === 'Total Material Purchases' || selectedSegment === 'Total Labour Charges';
  // Open the same popup from either a pie slice or a stat card
  const openSegmentDialog = (segment) => {
    setSelectedVendor(null);
    setVendorDrillDownData([]);
    setSearchQuery('');
    setBreakdownTabValue(0);
    setClickedArc(null);
    setSelectedSegment(segment);
    setDialogOpen(true);
  };

  // --- Mobile breakdown: colors, totals, list rows, drill-down ---
  const segColor = selectedSegment === 'Total Material Purchases' ? '#1E40AF'
    : selectedSegment === 'Total Labour Charges' ? '#DB2777'
    : selectedSegment === 'Total Revenue' ? '#C4B106' : '#059669';
  const segColorLight = ({ '#1E40AF': '#3b6fe0', '#DB2777': '#f24d97', '#C4B106': '#e0c521', '#059669': '#10b981' })[segColor] || segColor;
  const isMaterialSeg = selectedSegment === 'Total Material Purchases';
  const getSegTotal = () => selectedSegment === 'Total Material Purchases' ? aggregatedData.totalMaterialPurchases
    : selectedSegment === 'Total Labour Charges' ? aggregatedData.totalLabourCharges
    : selectedSegment === 'Total Revenue' ? rev : aggregatedData.netProfit;
  const drillVendor = (vendorName) => {
    const key = `${vendorName}_${isMaterialSeg ? 'material' : 'labour'}`;
    setSelectedVendor(vendorName);
    setVendorDrillDownData(vendorClientMap[key] || []);
    setSearchQuery('');
  };
  const mobileRows = (() => {
    const q = searchQuery.toLowerCase();
    if (!hasBreakdown) {
      return [
        { name: 'Material Purchases', amount: aggregatedData.totalMaterialPurchases, color: '#1E40AF' },
        { name: 'Labour Charges', amount: aggregatedData.totalLabourCharges, color: '#DB2777' },
        { name: 'Gross Profit', amount: aggregatedData.netProfit, color: '#059669' },
      ];
    }
    if (breakdownTabValue === 0 && selectedVendor) {
      return vendorDrillDownData.filter(i => i.clientName.toLowerCase().includes(q)).sort((a, b) => b.amount - a.amount).map(i => ({ name: i.clientName, amount: i.amount }));
    }
    if (breakdownTabValue === 0) {
      const list = isMaterialSeg ? breakdownData.materialPurchases : breakdownData.labourCharges;
      return list.filter(i => i.vendorName.toLowerCase().includes(q)).sort((a, b) => b.amount - a.amount).map(i => ({ name: i.vendorName, amount: i.amount, clickable: true }));
    }
    const list = isMaterialSeg ? clientWiseBreakdownData.materialPurchases : clientWiseBreakdownData.labourCharges;
    return list.filter(i => i.clientName.toLowerCase().includes(q)).sort((a, b) => b.amount - a.amount).map(i => ({ name: i.clientName, amount: i.amount }));
  })();
  const mobileTotal = !hasBreakdown ? rev
    : (breakdownTabValue === 0 && selectedVendor) ? vendorDrillDownData.reduce((s, i) => s + i.amount, 0)
    : getSegTotal();

  return (
    <Box sx={{ padding: { xs: "6px 2px 0", md: "30px" } }}>
      {/* Stat cards now render in the page hero above (see clients.js) —
          fed by onStatsReady so we don't duplicate the aggregation UI. */}

      {/* Pie Chart */}
      <Box sx={{ height: { xs: "340px", md: "480px" }, position: "relative", maxWidth: "900px", margin: "0 auto" }}>
        {/* Center Label for Total Revenue */}
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            textAlign: 'center',
            pointerEvents: 'none',
            zIndex: 10
          }}
        >
          <Typography
            sx={{
              fontSize: { xs: '11px', md: '16px' },
              fontWeight: 600,
              color: '#98a2b3',
              letterSpacing: { xs: '0.06em', md: 0 },
              textTransform: { xs: 'uppercase', md: 'none' },
              fontFamily: 'Roboto, Arial, sans-serif',
              marginBottom: { xs: '2px', md: '4px' }
            }}
          >
            Total Revenue
          </Typography>
          <Typography
            sx={{
              fontSize: { xs: '22px', md: '24px' },
              fontWeight: 700,
              color: '#101828',
              fontFamily: 'Roboto, Arial, sans-serif'
            }}
          >
            {isMobile ? compactINR(aggregatedData.totalRevenue) : '₹' + Math.round(aggregatedData.totalRevenue || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}
          </Typography>
        </Box>
        <ResponsivePie
          data={[
            {
              id: 'Total Material Purchases',
              label: 'Total Material Purchases',
              value: aggregatedData.totalMaterialPurchases,
              color: '#1E40AF'
            },
            {
              id: 'Total Labour Charges',
              label: 'Total Labour Charges',
              value: aggregatedData.totalLabourCharges,
              color: '#DB2777'
            },
            {
              id: 'Gross Profit',
              label: 'Gross Profit',
              value: aggregatedData.netProfit,
              color: '#059669'
            }
          ]}
          margin={isMobile ? { top: 24, right: 16, bottom: 40, left: 16 } : { top: 60, right: 180, bottom: 60, left: 180 }}
          innerRadius={0.65}
          padAngle={2}
          cornerRadius={8}
          activeId={isMobile ? undefined : (clickedArc || hoveredArc)}
          activeOuterRadiusOffset={isMobile ? 0 : 30}
          activeInnerRadiusOffset={isMobile ? 0 : 15}
          borderWidth={0}
          colors={{ datum: 'data.color' }}
          enableArcLinkLabels={!isMobile}
          arcLinkLabelsSkipAngle={10}
          arcLinkLabelsTextColor="#1a1a1a"
          arcLinkLabelsThickness={3}
          arcLinkLabelsColor={{ from: 'color' }}
          arcLinkLabel={(d) => clickedArc === d.id ? '' : d.label}
          arcLinkLabelsStraightLength={24}
          arcLinkLabelsDiagonalLength={16}
          arcLinkLabelsOffset={4}
          onMouseEnter={(node) => {
            setHoveredArc(node.id);
          }}
          onMouseLeave={() => {
            setHoveredArc(null);
          }}
          onClick={(node) => {
            // Mobile: no expand/zoom — a tap opens the breakdown directly (native-app feel)
            if (isMobile) {
              if (node.id !== 'Gross Profit') {
                setSelectedSegment(node.id);
                setDialogOpen(true);
              }
              return;
            }
            if (clickedArc === node.id) {
              // Clicking on already zoomed arc - zoom out, don't open popup
              setClickedArc(null);
            } else {
              // First click - zoom in
              setClickedArc(node.id);
              // Only open popup for Material Purchases and Labour Charges, not Gross Profit
              if (node.id !== 'Gross Profit') {
                setSelectedSegment(node.id);
                setDialogOpen(true);
              }
            }
          }}
          arcLabel={(d) => `${((d.value / aggregatedData.totalRevenue) * 100).toFixed(1)}%`}
          arcLabelsSkipAngle={10}
          arcLabelsTextColor="#ffffff"
          motionConfig="wobbly"
          transitionMode="startAngle"
          theme={{
            labels: {
              text: {
                fontSize: isMobile ? 11 : 14,
                fontWeight: 700,
                fill: '#ffffff',
                textShadow: '0 1px 3px rgba(0,0,0,0.55)',
                fontFamily: 'Roboto, Arial, sans-serif'
              }
            },
            legends: {
              text: {
                fontSize: 14,
                fontWeight: 600,
                fill: '#1a1a1a',
                fontFamily: 'Roboto, Arial, sans-serif'
              }
            }
          }}
        />
      </Box>

      {/* Stat cards below the pie (mobile) */}
      <Box sx={{ display: { xs: "grid", md: "none" }, gridTemplateColumns: "1fr 1fr", gap: 1.25, mt: 1.5, px: 0.5 }}>
        {statCards.map((card) => (
          <Box
            key={card.label}
            onClick={() => openSegmentDialog(card.segment)}
            sx={{
              background: "#f9fafb",
              borderRadius: "16px",
              p: 1.75,
              boxShadow: "0 1px 2px rgba(16, 24, 40, 0.04)",
              border: "1px solid #eef0f3",
              cursor: "pointer",
              transition: "transform 0.12s ease, box-shadow 0.2s ease",
              "&:active": { transform: "scale(0.97)", boxShadow: "0 1px 2px rgba(16,24,40,0.08)" },
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 0.75 }}>
              <Box sx={{ width: "10px", height: "10px", borderRadius: "50%", background: card.color, flexShrink: 0 }} />
              <Typography sx={{ fontSize: "11.5px", fontWeight: 600, color: "#667085", fontFamily: "Roboto, sans-serif", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                {card.label}
              </Typography>
            </Box>
            <Typography sx={{ fontSize: "17px", fontWeight: 700, color: "#101828", fontFamily: "Roboto, sans-serif", lineHeight: 1.2 }}>
              {compactINR(card.value)}
            </Typography>
            <Typography sx={{ fontSize: "10.5px", fontWeight: 600, color: card.color, mt: "3px", fontFamily: "Roboto, sans-serif" }}>
              {card.sub}
            </Typography>
          </Box>
        ))}
      </Box>

      {/* Breakdown Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={() => {
          setDialogOpen(false);
          setSearchQuery('');
        }}
        fullScreen={isMobile}
        maxWidth={false}
        PaperProps={{
          sx: {
            width: { xs: '100%', md: '80%' },
            height: { xs: '100%', md: '80%' },
            maxWidth: { xs: '100%', md: '80%' },
            maxHeight: { xs: '100%', md: '80%' },
          }
        }}
      >
        {isMobile ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', background: '#f4f5f7' }}>
            {/* Gradient hero */}
            <Box sx={{ background: `linear-gradient(155deg, ${segColorLight} 0%, ${segColor} 100%)`, color: '#fff', pt: 'max(16px, env(safe-area-inset-top))', px: 2.5, pb: 2.75, borderBottomLeftRadius: '26px', borderBottomRightRadius: '26px', boxShadow: `0 8px 24px ${segColor}55` }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                <IconButton
                  onClick={() => {
                    if (selectedVendor) { setSelectedVendor(null); setVendorDrillDownData([]); setSearchQuery(''); }
                    else { setDialogOpen(false); setSearchQuery(''); }
                  }}
                  sx={{ color: '#fff', ml: '-8px' }}
                >
                  <ArrowBackIcon />
                </IconButton>
                <Typography noWrap sx={{ fontSize: '15px', fontWeight: 600, fontFamily: 'Roboto, Arial, sans-serif', maxWidth: '62%' }}>
                  {selectedVendor ? selectedVendor : selectedSegment}
                </Typography>
                <IconButton onClick={() => { setDialogOpen(false); setSearchQuery(''); }} sx={{ color: '#fff', mr: '-8px' }}>
                  <CloseIcon />
                </IconButton>
              </Box>
              <Typography sx={{ fontSize: '11px', fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', opacity: 0.85, fontFamily: 'Roboto, Arial, sans-serif' }}>
                {selectedVendor ? 'Total from this vendor' : `Total ${selectedSegment}`}
              </Typography>
              <Typography sx={{ fontSize: '30px', fontWeight: 800, lineHeight: 1.15, fontFamily: 'Roboto, Arial, sans-serif' }}>
                {compactINR(mobileTotal)}
              </Typography>
              {hasBreakdown && !selectedVendor && (
                <Typography sx={{ fontSize: '12px', fontWeight: 600, opacity: 0.9, mt: '2px', fontFamily: 'Roboto, Arial, sans-serif' }}>
                  {pctOf(getSegTotal())}% of total revenue
                </Typography>
              )}
            </Box>

            {/* Segmented control */}
            {hasBreakdown && !selectedVendor && (
              <Box sx={{ display: 'flex', gap: '4px', background: '#e7e9ee', borderRadius: '14px', p: '4px', mx: 2, mt: 2 }}>
                {['Vendor-Wise', 'Client-Wise'].map((lbl, idx) => (
                  <Box
                    key={lbl}
                    onClick={() => { setBreakdownTabValue(idx); setSearchQuery(''); }}
                    sx={{ flex: 1, textAlign: 'center', py: '9px', borderRadius: '11px', fontSize: '12.5px', fontWeight: 700, fontFamily: 'Roboto, Arial, sans-serif', cursor: 'pointer', transition: 'all 0.2s ease', background: breakdownTabValue === idx ? '#fff' : 'transparent', color: breakdownTabValue === idx ? segColor : '#667085', boxShadow: breakdownTabValue === idx ? '0 1px 4px rgba(16,24,40,0.12)' : 'none' }}
                  >
                    {lbl}
                  </Box>
                ))}
              </Box>
            )}

            {/* Search */}
            {hasBreakdown && (
              <Box sx={{ mx: 2, mt: 1.5, display: 'flex', alignItems: 'center', gap: 1, background: '#fff', border: '1px solid #eaecf0', borderRadius: '12px', px: 1.5 }}>
                <SearchIcon sx={{ fontSize: 19, color: '#98a2b3' }} />
                <input
                  type="text"
                  placeholder={selectedVendor ? 'Search clients' : (breakdownTabValue === 0 ? 'Search vendors' : 'Search clients')}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  style={{ flex: 1, border: 'none', outline: 'none', padding: '11px 0', fontSize: '13.5px', fontFamily: 'Roboto, Arial, sans-serif', background: 'transparent' }}
                />
              </Box>
            )}

            {/* Ranked list */}
            <Box sx={{ flex: 1, overflowY: 'auto', px: 2, pt: 1.75, pb: 'max(16px, env(safe-area-inset-bottom))' }}>
              {mobileRows.length === 0 ? (
                <Typography sx={{ textAlign: 'center', color: '#98a2b3', fontSize: '13px', mt: 4, fontFamily: 'Roboto, Arial, sans-serif' }}>
                  No records found
                </Typography>
              ) : (
                mobileRows.map((row, i) => {
                  const rowColor = row.color || segColor;
                  const pct = mobileTotal > 0 ? (row.amount / mobileTotal) * 100 : 0;
                  return (
                    <Box
                      key={i}
                      onClick={row.clickable ? () => drillVendor(row.name) : undefined}
                      sx={{ display: 'flex', alignItems: 'center', gap: 1.5, background: '#fff', borderRadius: '16px', p: 1.75, mb: 1.25, boxShadow: '0 1px 3px rgba(16,24,40,0.06)', border: '1px solid #f0f1f3', cursor: row.clickable ? 'pointer' : 'default', transition: 'transform 0.12s ease', ...(row.clickable ? { '&:active': { transform: 'scale(0.98)' } } : {}) }}
                    >
                      <Box sx={{ width: 40, height: 40, borderRadius: '12px', background: `${rowColor}18`, color: rowColor, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: '15px', fontFamily: 'Roboto, Arial, sans-serif', flexShrink: 0 }}>
                        {row.name ? row.name.trim().charAt(0).toUpperCase() : '#'}
                      </Box>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                          <Typography noWrap sx={{ fontSize: '13.5px', fontWeight: 600, color: '#101828', fontFamily: 'Roboto, Arial, sans-serif' }}>
                            {row.name}
                          </Typography>
                          <Typography sx={{ fontSize: '13.5px', fontWeight: 700, color: '#101828', whiteSpace: 'nowrap', fontFamily: 'Roboto, Arial, sans-serif' }}>
                            ₹{Number(row.amount).toLocaleString('en-IN')}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: '7px' }}>
                          <Box sx={{ flex: 1, height: '6px', borderRadius: '3px', background: '#f0f1f3', overflow: 'hidden' }}>
                            <Box sx={{ width: `${Math.min(100, pct)}%`, height: '100%', borderRadius: '3px', background: rowColor }} />
                          </Box>
                          <Typography sx={{ fontSize: '11px', fontWeight: 600, color: '#98a2b3', minWidth: '38px', textAlign: 'right', fontFamily: 'Roboto, Arial, sans-serif' }}>
                            {pct.toFixed(1)}%
                          </Typography>
                        </Box>
                      </Box>
                      {row.clickable && <ChevronRightIcon sx={{ color: '#c4c9d2', fontSize: 20, flexShrink: 0 }} />}
                    </Box>
                  );
                })
              )}
            </Box>
          </Box>
        ) : (
        <>
        <DialogTitle sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          background: (() => {
            const seg = selectedSegment === 'Total Material Purchases' ? '#1E40AF' :
                        selectedSegment === 'Total Labour Charges' ? '#DB2777' :
                        selectedSegment === 'Total Revenue' ? '#C4B106' : '#059669';
            return `linear-gradient(155deg, ${seg} 0%, ${seg}cc 100%)`;
          })(),
          color: 'white',
          fontSize: { xs: '16px', md: '17px' },
          fontWeight: { xs: 700, md: 800 },
          fontFamily: 'Roboto, Arial, sans-serif',
          padding: { xs: 'max(16px, env(safe-area-inset-top)) 18px 14px', md: '18px 24px' },
          minHeight: '48px'
        }}>
          {selectedSegment} Breakdown
          <IconButton
            onClick={() => {
              setDialogOpen(false);
              setSearchQuery('');
            }}
            sx={{ color: 'white', padding: '4px', background: 'rgba(255,255,255,0.15)', '&:hover': { background: 'rgba(255,255,255,0.25)' } }}
          >
            <CloseIcon sx={{ fontSize: '20px' }} />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ padding: { xs: '14px', md: '24px' } }}>
          {hasBreakdown ? (
          <>
          {/* Vendor-Wise / Client-Wise — segmented pill control, not underline Tabs */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box sx={{ display: "inline-flex", gap: "4px", background: "#eef2f7", borderRadius: "13px", p: "4px" }}>
              {["Vendor-Wise Breakup", "Client-Wise Breakup"].map((label, idx) => {
                const active = breakdownTabValue === idx;
                return (
                  <Box
                    key={label}
                    onClick={() => { setBreakdownTabValue(idx); setSearchQuery(''); }}
                    sx={{
                      px: 2.25,
                      py: "8px",
                      borderRadius: "10px",
                      fontSize: "13px",
                      fontWeight: active ? 700 : 600,
                      fontFamily: "Roboto, Arial, sans-serif",
                      color: active ? "#1565C0" : "#5f6f86",
                      background: active ? "#ffffff" : "transparent",
                      boxShadow: active ? "0 1px 3px rgba(16,24,40,0.10)" : "none",
                      cursor: "pointer",
                      transition: "all 0.2s ease",
                      userSelect: "none",
                      whiteSpace: "nowrap",
                      "&:hover": { color: "#1565C0" },
                    }}
                  >
                    {label}
                  </Box>
                );
              })}
            </Box>

            <Box sx={{ marginLeft: "auto", width: "220px" }} className="client-search-bar">
              <SearchIcon className="search-icon-wrapper" />
              <InputBase
                className="styled-input-base"
                placeholder="Search…"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </Box>
          </Box>

          <TableContainer component={Paper} sx={{ boxShadow: "0 1px 2px rgba(16,24,40,0.05)", border: "1px solid #eef2f6", borderRadius: "16px", overflow: "hidden", marginTop: '16px', width: { xs: '100%', md: '75%' } }}>
            <Table sx={{ "& tbody tr:hover": { backgroundColor: "#f8fbff" } }}>
              <TableHead>
                <TableRow>
                  <TableCell
                    sx={{
                      width: "5%",
                      textAlign: "center",
                      padding: "12px",
                      color: "#5f6f86",
                      backgroundColor: "#f5f8fd",
                      textTransform: "uppercase",
                      letterSpacing: "0.07em",
                      fontSize: "11px",
                      fontWeight: 700,
                      fontFamily: "Roboto, Arial, sans-serif",
                      borderRight: "1px solid #e0e0e0"
                    }}
                  >
                    S/No
                  </TableCell>
                  <TableCell
                    sx={{
                      width: "40%",
                      textAlign: "left",
                      padding: "12px",
                      color: "#5f6f86",
                      backgroundColor: "#f5f8fd",
                      textTransform: "uppercase",
                      letterSpacing: "0.07em",
                      fontSize: "11px",
                      fontWeight: 700,
                      fontFamily: "Roboto, Arial, sans-serif",
                      borderRight: "1px solid #e0e0e0"
                    }}
                  >
                    {breakdownTabValue === 0 ? (selectedVendor ? 'Client-wise Breakup' : 'Vendor Name') : 'Client Name'}
                    {breakdownTabValue === 0 && selectedVendor && (
                      <span
                        onClick={() => {
                          setSelectedVendor(null);
                          setVendorDrillDownData([]);
                          setSearchQuery('');
                        }}
                        style={{
                          cursor: "pointer",
                          color: "white",
                          fontSize: "13px",
                          fontWeight: 500,
                          fontFamily: "Roboto, Arial, sans-serif",
                          float: "right",
                          textDecoration: "none",
                          display: "inline-flex",
                          alignItems: "center",
                          gap: "4px"
                        }}
                        onMouseEnter={(e) => e.target.style.textDecoration = "underline"}
                        onMouseLeave={(e) => e.target.style.textDecoration = "none"}
                      >
                        <ArrowBackIcon sx={{ fontSize: "16px" }} />
                        Go Back
                      </span>
                    )}
                  </TableCell>
                  <TableCell
                    sx={{
                      width: "15%",
                      textAlign: "left",
                      padding: "12px",
                      color: "#5f6f86",
                      backgroundColor: "#f5f8fd",
                      textTransform: "uppercase",
                      letterSpacing: "0.07em",
                      fontSize: "11px",
                      fontWeight: 700,
                      fontFamily: "Roboto, Arial, sans-serif",
                      borderRight: "1px solid #e0e0e0"
                    }}
                  >
                    Amount
                  </TableCell>
                  <TableCell
                    sx={{
                      width: "15%",
                      textAlign: "left",
                      padding: "12px",
                      color: "#5f6f86",
                      backgroundColor: "#f5f8fd",
                      textTransform: "uppercase",
                      letterSpacing: "0.07em",
                      fontSize: "11px",
                      fontWeight: 700,
                      fontFamily: "Roboto, Arial, sans-serif"
                    }}
                  >
                    %
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {/* Vendor-Wise Breakdown */}
                {breakdownTabValue === 0 && selectedSegment === 'Total Material Purchases' && (
                  <>
                    {!selectedVendor && breakdownData.materialPurchases
                      .filter(item => item.vendorName.toLowerCase().includes(searchQuery.toLowerCase()))
                      .sort((a, b) => b.amount - a.amount)
                      .map((item, index) => (
                      <TableRow key={index}>
                        <TableCell sx={{ textAlign: "center", fontSize: "13px", fontFamily: "Roboto, Arial, sans-serif", padding: "12px", borderRight: "1px solid #e0e0e0" }}>
                          {index + 1}
                        </TableCell>
                        <TableCell
                          sx={{
                            textAlign: "left",
                            fontSize: "13px",
                            fontFamily: "Roboto, Arial, sans-serif",
                            padding: "12px",
                            borderRight: "1px solid #e0e0e0",
                            color: "#1E40AF",
                            cursor: "pointer",
                            "&:hover": {
                              textDecoration: "underline"
                            }
                          }}
                          onClick={() => {
                            // Handle vendor click - drill down to show clients for this vendor
                            const vendorKey = `${item.vendorName}_material`;
                            const clientsForVendor = vendorClientMap[vendorKey] || [];
                            setSelectedVendor(item.vendorName);
                            setVendorDrillDownData(clientsForVendor);
                            setSearchQuery('');
                          }}
                        >
                          {item.vendorName}
                        </TableCell>
                        <TableCell sx={{ textAlign: "left", fontSize: "13px", fontFamily: "Roboto, Arial, sans-serif", padding: "12px", borderRight: "1px solid #e0e0e0" }}>
                          ₹ {item.amount.toLocaleString('en-IN')}
                        </TableCell>
                        <TableCell sx={{ textAlign: "left", fontSize: "13px", fontFamily: "Roboto, Arial, sans-serif", padding: "12px" }}>
                          {((item.amount / aggregatedData.totalMaterialPurchases) * 100).toFixed(2)}%
                        </TableCell>
                      </TableRow>
                    ))}
                    {/* Client-level drill-down when vendor is selected */}
                    {selectedVendor && vendorDrillDownData
                      .filter(item => item.clientName.toLowerCase().includes(searchQuery.toLowerCase()))
                      .sort((a, b) => b.amount - a.amount)
                      .map((item, index) => (
                      <TableRow key={index}>
                        <TableCell sx={{ textAlign: "center", fontSize: "13px", fontFamily: "Roboto, Arial, sans-serif", padding: "12px", borderRight: "1px solid #e0e0e0" }}>
                          {index + 1}
                        </TableCell>
                        <TableCell
                          sx={{
                            textAlign: "left",
                            fontSize: "13px",
                            fontFamily: "Roboto, Arial, sans-serif",
                            padding: "12px",
                            borderRight: "1px solid #e0e0e0",
                            color: "#1E40AF"
                          }}
                        >
                          {item.clientName}
                        </TableCell>
                        <TableCell sx={{ textAlign: "left", fontSize: "13px", fontFamily: "Roboto, Arial, sans-serif", padding: "12px", borderRight: "1px solid #e0e0e0" }}>
                          ₹ {item.amount.toLocaleString('en-IN')}
                        </TableCell>
                        <TableCell sx={{ textAlign: "left", fontSize: "13px", fontFamily: "Roboto, Arial, sans-serif", padding: "12px" }}>
                          {(() => {
                            const totalForVendor = vendorDrillDownData.reduce((sum, client) => sum + client.amount, 0);
                            return ((item.amount / totalForVendor) * 100).toFixed(2);
                          })()}%
                        </TableCell>
                      </TableRow>
                    ))}
                    {/* Total Row */}
                    <TableRow>
                      <TableCell colSpan={2} sx={{ textAlign: "center", fontSize: "13px", fontWeight: 600, fontFamily: "Roboto, Arial, sans-serif", padding: "12px", borderRight: "1px solid #e0e0e0", backgroundColor: "#f5f5f5" }}>
                        Total
                      </TableCell>
                      <TableCell sx={{ textAlign: "left", fontSize: "13px", fontWeight: 600, fontFamily: "Roboto, Arial, sans-serif", padding: "12px", borderRight: "1px solid #e0e0e0", backgroundColor: "#f5f5f5" }}>
                        ₹ {selectedVendor ? vendorDrillDownData.reduce((sum, client) => sum + client.amount, 0).toLocaleString('en-IN') : aggregatedData.totalMaterialPurchases.toLocaleString('en-IN')}
                      </TableCell>
                      <TableCell sx={{ textAlign: "left", fontSize: "13px", fontWeight: 600, fontFamily: "Roboto, Arial, sans-serif", padding: "12px", backgroundColor: "#f5f5f5" }}>
                        100.00%
                      </TableCell>
                    </TableRow>
                  </>
                )}
                {breakdownTabValue === 0 && selectedSegment === 'Total Labour Charges' && (
                  <>
                    {!selectedVendor && breakdownData.labourCharges
                      .filter(item => item.vendorName.toLowerCase().includes(searchQuery.toLowerCase()))
                      .sort((a, b) => b.amount - a.amount)
                      .map((item, index) => (
                      <TableRow key={index}>
                        <TableCell sx={{ textAlign: "center", fontSize: "13px", fontFamily: "Roboto, Arial, sans-serif", padding: "12px", borderRight: "1px solid #e0e0e0" }}>
                          {index + 1}
                        </TableCell>
                        <TableCell
                          sx={{
                            textAlign: "left",
                            fontSize: "13px",
                            fontFamily: "Roboto, Arial, sans-serif",
                            padding: "12px",
                            borderRight: "1px solid #e0e0e0",
                            color: "#1E40AF",
                            cursor: "pointer",
                            "&:hover": {
                              textDecoration: "underline"
                            }
                          }}
                          onClick={() => {
                            // Handle vendor click - drill down to show clients for this vendor
                            const vendorKey = `${item.vendorName}_labour`;
                            const clientsForVendor = vendorClientMap[vendorKey] || [];
                            setSelectedVendor(item.vendorName);
                            setVendorDrillDownData(clientsForVendor);
                            setSearchQuery('');
                          }}
                        >
                          {item.vendorName}
                        </TableCell>
                        <TableCell sx={{ textAlign: "left", fontSize: "13px", fontFamily: "Roboto, Arial, sans-serif", padding: "12px", borderRight: "1px solid #e0e0e0" }}>
                          ₹ {item.amount.toLocaleString('en-IN')}
                        </TableCell>
                        <TableCell sx={{ textAlign: "left", fontSize: "13px", fontFamily: "Roboto, Arial, sans-serif", padding: "12px" }}>
                          {((item.amount / aggregatedData.totalLabourCharges) * 100).toFixed(2)}%
                        </TableCell>
                      </TableRow>
                    ))}
                    {/* Client-level drill-down when vendor is selected */}
                    {selectedVendor && vendorDrillDownData
                      .filter(item => item.clientName.toLowerCase().includes(searchQuery.toLowerCase()))
                      .sort((a, b) => b.amount - a.amount)
                      .map((item, index) => (
                      <TableRow key={index}>
                        <TableCell sx={{ textAlign: "center", fontSize: "13px", fontFamily: "Roboto, Arial, sans-serif", padding: "12px", borderRight: "1px solid #e0e0e0" }}>
                          {index + 1}
                        </TableCell>
                        <TableCell
                          sx={{
                            textAlign: "left",
                            fontSize: "13px",
                            fontFamily: "Roboto, Arial, sans-serif",
                            padding: "12px",
                            borderRight: "1px solid #e0e0e0",
                            color: "#1E40AF"
                          }}
                        >
                          {item.clientName}
                        </TableCell>
                        <TableCell sx={{ textAlign: "left", fontSize: "13px", fontFamily: "Roboto, Arial, sans-serif", padding: "12px", borderRight: "1px solid #e0e0e0" }}>
                          ₹ {item.amount.toLocaleString('en-IN')}
                        </TableCell>
                        <TableCell sx={{ textAlign: "left", fontSize: "13px", fontFamily: "Roboto, Arial, sans-serif", padding: "12px" }}>
                          {(() => {
                            const totalForVendor = vendorDrillDownData.reduce((sum, client) => sum + client.amount, 0);
                            return ((item.amount / totalForVendor) * 100).toFixed(2);
                          })()}%
                        </TableCell>
                      </TableRow>
                    ))}
                    {/* Total Row */}
                    <TableRow>
                      <TableCell colSpan={2} sx={{ textAlign: "center", fontSize: "13px", fontWeight: 600, fontFamily: "Roboto, Arial, sans-serif", padding: "12px", borderRight: "1px solid #e0e0e0", backgroundColor: "#f5f5f5" }}>
                        Total
                      </TableCell>
                      <TableCell sx={{ textAlign: "left", fontSize: "13px", fontWeight: 600, fontFamily: "Roboto, Arial, sans-serif", padding: "12px", borderRight: "1px solid #e0e0e0", backgroundColor: "#f5f5f5" }}>
                        ₹ {selectedVendor ? vendorDrillDownData.reduce((sum, client) => sum + client.amount, 0).toLocaleString('en-IN') : aggregatedData.totalLabourCharges.toLocaleString('en-IN')}
                      </TableCell>
                      <TableCell sx={{ textAlign: "left", fontSize: "13px", fontWeight: 600, fontFamily: "Roboto, Arial, sans-serif", padding: "12px", backgroundColor: "#f5f5f5" }}>
                        100.00%
                      </TableCell>
                    </TableRow>
                  </>
                )}

                {/* Client-Wise Breakdown */}
                {breakdownTabValue === 1 && selectedSegment === 'Total Material Purchases' && (
                  <>
                    {clientWiseBreakdownData.materialPurchases
                      .filter(item => item.clientName.toLowerCase().includes(searchQuery.toLowerCase()))
                      .sort((a, b) => b.amount - a.amount)
                      .map((item, index) => (
                      <TableRow key={index}>
                        <TableCell sx={{ textAlign: "center", fontSize: "13px", fontFamily: "Roboto, Arial, sans-serif", padding: "12px", borderRight: "1px solid #e0e0e0" }}>
                          {index + 1}
                        </TableCell>
                        <TableCell
                          sx={{
                            textAlign: "left",
                            fontSize: "13px",
                            fontFamily: "Roboto, Arial, sans-serif",
                            padding: "12px",
                            borderRight: "1px solid #e0e0e0",
                            color: "#1E40AF",
                            cursor: "pointer",
                            "&:hover": {
                              textDecoration: "underline"
                            }
                          }}
                          onClick={() => {
                            // Handle client click
                            console.log('Client clicked:', item.clientName);
                          }}
                        >
                          {item.clientName}
                        </TableCell>
                        <TableCell sx={{ textAlign: "left", fontSize: "13px", fontFamily: "Roboto, Arial, sans-serif", padding: "12px", borderRight: "1px solid #e0e0e0" }}>
                          ₹ {item.amount.toLocaleString('en-IN')}
                        </TableCell>
                        <TableCell sx={{ textAlign: "left", fontSize: "13px", fontFamily: "Roboto, Arial, sans-serif", padding: "12px" }}>
                          {((item.amount / aggregatedData.totalMaterialPurchases) * 100).toFixed(2)}%
                        </TableCell>
                      </TableRow>
                    ))}
                    {/* Total Row */}
                    <TableRow>
                      <TableCell colSpan={2} sx={{ textAlign: "center", fontSize: "13px", fontWeight: 600, fontFamily: "Roboto, Arial, sans-serif", padding: "12px", borderRight: "1px solid #e0e0e0", backgroundColor: "#f5f5f5" }}>
                        Total
                      </TableCell>
                      <TableCell sx={{ textAlign: "left", fontSize: "13px", fontWeight: 600, fontFamily: "Roboto, Arial, sans-serif", padding: "12px", borderRight: "1px solid #e0e0e0", backgroundColor: "#f5f5f5" }}>
                        ₹ {aggregatedData.totalMaterialPurchases.toLocaleString('en-IN')}
                      </TableCell>
                      <TableCell sx={{ textAlign: "left", fontSize: "13px", fontWeight: 600, fontFamily: "Roboto, Arial, sans-serif", padding: "12px", backgroundColor: "#f5f5f5" }}>
                        100.00%
                      </TableCell>
                    </TableRow>
                  </>
                )}
                {breakdownTabValue === 1 && selectedSegment === 'Total Labour Charges' && (
                  <>
                    {clientWiseBreakdownData.labourCharges
                      .filter(item => item.clientName.toLowerCase().includes(searchQuery.toLowerCase()))
                      .sort((a, b) => b.amount - a.amount)
                      .map((item, index) => (
                      <TableRow key={index}>
                        <TableCell sx={{ textAlign: "center", fontSize: "13px", fontFamily: "Roboto, Arial, sans-serif", padding: "12px", borderRight: "1px solid #e0e0e0" }}>
                          {index + 1}
                        </TableCell>
                        <TableCell
                          sx={{
                            textAlign: "left",
                            fontSize: "13px",
                            fontFamily: "Roboto, Arial, sans-serif",
                            padding: "12px",
                            borderRight: "1px solid #e0e0e0",
                            color: "#1E40AF",
                            cursor: "pointer",
                            "&:hover": {
                              textDecoration: "underline"
                            }
                          }}
                          onClick={() => {
                            // Handle client click
                            console.log('Client clicked:', item.clientName);
                          }}
                        >
                          {item.clientName}
                        </TableCell>
                        <TableCell sx={{ textAlign: "left", fontSize: "13px", fontFamily: "Roboto, Arial, sans-serif", padding: "12px", borderRight: "1px solid #e0e0e0" }}>
                          ₹ {item.amount.toLocaleString('en-IN')}
                        </TableCell>
                        <TableCell sx={{ textAlign: "left", fontSize: "13px", fontFamily: "Roboto, Arial, sans-serif", padding: "12px" }}>
                          {((item.amount / aggregatedData.totalLabourCharges) * 100).toFixed(2)}%
                        </TableCell>
                      </TableRow>
                    ))}
                    {/* Total Row */}
                    <TableRow>
                      <TableCell colSpan={2} sx={{ textAlign: "center", fontSize: "13px", fontWeight: 600, fontFamily: "Roboto, Arial, sans-serif", padding: "12px", borderRight: "1px solid #e0e0e0", backgroundColor: "#f5f5f5" }}>
                        Total
                      </TableCell>
                      <TableCell sx={{ textAlign: "left", fontSize: "13px", fontWeight: 600, fontFamily: "Roboto, Arial, sans-serif", padding: "12px", borderRight: "1px solid #e0e0e0", backgroundColor: "#f5f5f5" }}>
                        ₹ {aggregatedData.totalLabourCharges.toLocaleString('en-IN')}
                      </TableCell>
                      <TableCell sx={{ textAlign: "left", fontSize: "13px", fontWeight: 600, fontFamily: "Roboto, Arial, sans-serif", padding: "12px", backgroundColor: "#f5f5f5" }}>
                        100.00%
                      </TableCell>
                    </TableRow>
                  </>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          </>
          ) : (
            <Box sx={{ mt: 1 }}>
              <Typography sx={{ fontSize: { xs: '12px', md: '13px' }, color: '#98a2b3', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', mb: 1.5 }}>
                Financial Summary
              </Typography>
              {[
                { label: 'Total Revenue', value: rev, color: '#C4B106' },
                { label: 'Material Purchases', value: aggregatedData.totalMaterialPurchases, color: '#1E40AF' },
                { label: 'Labour Charges', value: aggregatedData.totalLabourCharges, color: '#DB2777' },
                { label: 'Gross Profit', value: aggregatedData.netProfit, color: '#059669' },
              ].map((row) => (
                <Box key={row.label} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 1.5, borderBottom: '1px solid #f0f1f3' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{ width: 10, height: 10, borderRadius: '50%', background: row.color }} />
                    <Typography sx={{ fontSize: '13.5px', color: '#344054', fontWeight: 500, fontFamily: 'Roboto, Arial, sans-serif' }}>{row.label}</Typography>
                  </Box>
                  <Typography sx={{ fontSize: '14px', fontWeight: 700, color: '#101828', fontFamily: 'Roboto, Arial, sans-serif' }}>
                    ₹{Number(row.value).toLocaleString('en-IN')}
                  </Typography>
                </Box>
              ))}
            </Box>
          )}
        </DialogContent>
        </>
        )}
      </Dialog>
    </Box>
  );
};

export default Summary;
